'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import type { CustomerInfo } from '../types'
import { EmailInput, PhoneInput } from '@/components/ui'
import { ONLINE_CHECKOUT_PROMO_BOX_ENABLED } from '@/lib/marketing/checkoutPromoBox'
import { isValidCustomerEmail } from '@/lib/validation/customerEmail'
import type { SubstitutionPref } from '@/lib/online-orders/substitutionPrefs'
import { SubstitutionPrefSelector } from './SubstitutionPrefSelector'

type Props = {
  customer: CustomerInfo
  setCustomer: (c: CustomerInfo) => void
  locationName: string
  /** % de la oferta de bienvenida; con oferta la casilla lo dice. null = sin oferta. */
  welcomePct?: number | null
  total: number
  accentColor: string
  onContinue: () => void
  onBack: () => void
  /**
   * «Si algo no está disponible» (spec sustituciones §4.2) vive acá, el paso
   * ANTES de Pago: Pago crea la sesión de cobro al montarse con la preferencia
   * que haya en ese momento, y con pago en dominio propio redirige enseguida —
   * elegirla en Pago no llegaba al pedido (visto en Empanada Factory, 2026-10-20).
   * Acá ya hay teléfono, así que «Preguntarme primero» se decide con datos reales.
   */
  substitutionsEnabled?: boolean
  substitutionPref?: SubstitutionPref
  onSubstitutionPrefChange?: (p: SubstitutionPref) => void
  contactAllowed?: boolean
}

// Must stay in sync with normalizePhone() in PaymentStep.tsx and the
// E.164 regex enforced by /api/online-orders (^\+\d{10,15}$). Reject early
// here so the customer sees a clear inline error instead of a generic
// "Invalid request" after they've already entered the card form.
function isPhoneValid(raw: string): boolean {
  const trimmed = raw.trim()
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length < 10 || digits.length > 15) return false
  return true
}

export function CustomerStep({
  customer,
  setCustomer,
  locationName,
  welcomePct = null,
  total,
  accentColor,
  onContinue,
  substitutionsEnabled = false,
  substitutionPref = 'similar',
  onSubstitutionPrefChange,
  contactAllowed = false,
  onBack,
}: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const tOrder = useTranslations('public.onlineOrder')
  const tCommon = useTranslations('common')
  const [touched, setTouched] = useState(false)

  const nameOk = customer.name.trim().length > 0
  const phoneOk = isPhoneValid(customer.phone)
  // Email is optional; only validate if user typed something. MISMA regla que
  // /session y la orden (lib/validation/customerEmail): la regex que había acá
  // aceptaba un punto antes de la arroba, que la orden rechaza DESPUÉS de cobrar.
  const emailRaw = customer.email.trim()
  const emailOk = emailRaw.length === 0 || isValidCustomerEmail(emailRaw)
  // Billing ZIP is optional (improves AVS / lowers interchange); only validate
  // the US 5 or 5+4 format when the customer actually typed something.
  const zipRaw = customer.billingZip.trim()
  const zipOk = zipRaw.length === 0 || /^\d{5}(-\d{4})?$/.test(zipRaw)
  const canContinue = nameOk && phoneOk && emailOk && zipOk

  function handleSubmit() {
    setTouched(true)
    if (!canContinue) return
    onContinue()
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← {t('back')}
        </button>
        <h2 className="flex-1 text-center text-xl font-bold text-gray-900 -ml-12">
          {tOrder('contactInfo')}
        </h2>
      </div>

      {/* Form */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        <div className="space-y-4">
          <Field
            label={t('nameLabel')}
            required
            error={touched && !nameOk ? t('nameError') : null}
          >
            <input
              type="text"
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              placeholder={t('namePlaceholder')}
              className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              autoComplete="name"
            />
          </Field>

          <Field
            label={t('phoneLabel')}
            required
            error={touched && !phoneOk ? t('phoneError') : null}
          >
            <PhoneInput
              value={customer.phone}
              onChange={(v) => setCustomer({ ...customer, phone: v })}
              placeholder="(555) 000-0000"
              className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            />
          </Field>

          <Field
            label={t('emailLabel')}
            error={touched && !emailOk ? t('emailError') : null}
          >
            <EmailInput
              value={customer.email}
              onChange={(v) => setCustomer({ ...customer, email: v })}
              placeholder={t('emailPlaceholder')}
              inputClassName="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            />
          </Field>

          <Field
            label={t('billingZipLabel')}
            error={touched && !zipOk ? t('billingZipError') : null}
          >
            <input
              type="text"
              value={customer.billingZip}
              onChange={(e) => setCustomer({ ...customer, billingZip: e.target.value })}
              placeholder="79945"
              className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              autoComplete="postal-code"
              inputMode="numeric"
              maxLength={10}
            />
          </Field>

          <Field label={t('pickupNoteLabel')}>
            <textarea
              value={customer.pickupNote}
              onChange={(e) =>
                setCustomer({ ...customer, pickupNote: e.target.value })
              }
              placeholder={t('pickupNotePlaceholder')}
              className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              rows={3}
              maxLength={500}
            />
          </Field>

          {/* A2P/TCPA — divulgación de SMS TRANSACCIONALES, sin casilla.
              El cliente escribe su teléfono arriba para recibir SU PROPIO pedido;
              el número, dado acá con esta divulgación a la vista, ES el
              consentimiento (mismo criterio que el recibo del POS —
              lib/online-orders/receiptConsent.ts — y el pedido de mesa por QR —
              lib/qr/tableCustomer.ts).

              Por qué se fue la casilla: era opcional, el 17% de los pedidos
              online no la marcaba y ese `false` se escribía en `customers`,
              silenciando ese teléfono para siempre en ese local (assertOptIn
              bloquea la fila con false) — también el recibo presencial del POS.
              En prod: 55 clientes mudos, CERO revocaciones reales.

              Por qué NO se volvió obligatoria: el programa registrado ante el
              carrier es 100% transaccional y declara que el consentimiento no es
              condición de compra (CTIA/TCPA). Exigirla contradiría lo declarado.

              La salida sigue viva y sigue siendo la única que silencia: STOP,
              que el webhook inbound de Twilio convierte en revocación real.
              Este texto tiene que seguir espejado en /sms-opt-in (evidencia para
              el carrier) y en docs/legal/a2p-reregistration-online-optin.md. */}
          <div
            className="rounded-2xl border p-4"
            style={{ borderColor: `${accentColor}40`, backgroundColor: `${accentColor}10` }}
          >
            <p className="text-xs text-gray-700 leading-relaxed">
              {t.rich('smsDisclaimer', {
                name: () => <span className="font-semibold">{locationName}</span>,
              })}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t('see')}{' '}
              <a
                href="/sms-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: accentColor }}
              >
                {t('smsTermsLink')}
              </a>{' '}
              {tCommon('and')}{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: accentColor }}
              >
                {t('privacyLink')}
              </a>
              .
            </p>
            {/* Salida del programa SMS en el mismo momento del consentimiento
                (rechazo A2P 30923: el consentimiento no puede ser condición de
                la compra). checkbox = exento del candado iOS de 16px (no abre
                teclado). Marcado, el servidor escribe una revocación real
                (false + sms_consent_at) — ver "Consentimiento SMS" en la ruta. */}
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={customer.smsDecline}
                onChange={(e) =>
                  setCustomer({
                    ...customer,
                    smsDecline: e.target.checked,
                    // Declinar los SMS de este pedido apaga también la casilla de
                    // promociones (ver el comentario de esa casilla, abajo). Sin
                    // esto, marcar las dos mandaba marketingOptIn=true al
                    // servidor y quedaba un consentimiento registrado que jamás
                    // se iba a honrar.
                    ...(e.target.checked ? { marketingOptIn: false } : {}),
                  })
                }
                className="h-4 w-4 rounded border-gray-300"
                style={{ accentColor }}
              />
              <span className="text-xs font-medium text-gray-600">{t('smsDeclineLabel')}</span>
            </label>
            {/* Apagada desde 2026-10-01: ver lib/marketing/checkoutPromoBox.ts.
                Casilla de MARKETING — opt-IN, separada de smsDecline arriba
                (que es un opt-OUT transaccional; otra cosa por completo).
                Nunca marcada por defecto: una casilla pre-marcada no es
                consentimiento expreso bajo TCPA y tira abajo el registro A2P
                del comercio. El texto nombra al RESTAURANTE (locationName), no
                a Redy — es su marca, su número y su campaña A2P.

                Separadas SÍ, pero no independientes: marcar las dos es una
                contradicción que el servidor resolvía en contra del comensal.
                Quedaba una fila `granted` en la bitácora y marketing_opt_in
                en true, mientras el contacto sale a GHL con dndSms=true
                permanente (lib/cypher/sync.ts: el STOP transaccional cierra
                los dos canales) — o sea, Redy guardaba evidencia de un
                consentimiento que había decidido no honrar nunca, y el
                comensal no recibía ninguna señal de eso. Con smsDecline
                marcada, esta casilla se apaga, se deshabilita y dice por qué.
                Se resuelve acá, en la UI, y no en el servidor: es el único
                lugar donde el comensal puede enterarse. */}
            {ONLINE_CHECKOUT_PROMO_BOX_ENABLED && (
              <label
                className={`flex items-start gap-2 mt-3 select-none ${
                  customer.smsDecline ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  // El `&& !smsDecline` es cinturón sobre tirantes: el handler de
                  // arriba ya baja `marketingOptIn` al declinar, pero así la
                  // casilla no puede pintarse marcada ni aunque el estado llegue
                  // contradictorio desde otro lado (p. ej. un prefill de reorder).
                  checked={customer.marketingOptIn && !customer.smsDecline}
                  disabled={customer.smsDecline}
                  onChange={(e) =>
                    setCustomer({ ...customer, marketingOptIn: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-gray-300 mt-0.5 disabled:cursor-not-allowed"
                  style={{ accentColor }}
                />
                <span className="text-xs font-medium text-gray-600">
                  {welcomePct !== null
                    ? t('marketingOptInLabelWelcome', { business: locationName, pct: welcomePct })
                    : t('marketingOptInLabel', { business: locationName })}
                  <span className="block text-[11px] text-gray-400 mt-0.5">
                    {/* Deshabilitada no se puede consentir, así que en su lugar va
                        el motivo: la divulgación (STOP/HELP/tarifas) vuelve sola
                        apenas la casilla se puede volver a marcar. */}
                    {customer.smsDecline ? t('marketingOptInBlocked') : t('marketingOptInFine')}
                  </span>
                </span>
              </label>
            )}
          </div>
          {substitutionsEnabled && onSubstitutionPrefChange && (
            <div className="mt-6">
              <SubstitutionPrefSelector
                value={substitutionPref}
                onChange={onSubstitutionPrefChange}
                contactAllowed={contactAllowed}
                accentColor={accentColor}
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        className="border-t border-gray-100 bg-white px-5 pt-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="text-gray-600">{tCommon('total')}</span>
          <span className="font-bold text-gray-900">${total.toFixed(2)}</span>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canContinue}
          className="w-full rounded-full py-4 text-white text-base font-bold transition hover:opacity-95 active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: accentColor }}
        >
          {t('continueToPayment')} →
        </button>
      </div>
    </div>
  )
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string
  required?: boolean
  error?: string | null
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
